/**
 * Backend Authentication Provider - Uses backend API for all auth operations
 */

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { authService } from '../../services/AuthService.js';
import { dataService } from '../../services/DataService.js';
import { apiService } from '../../services/ApiService.js';
import { changeLanguage, mapLocaleToLanguage } from '../../i18n';

const BackendAuthContext = createContext();

export const useBackendAuth = () => {
  const context = useContext(BackendAuthContext);
  if (!context) {
    throw new Error('useBackendAuth must be used within BackendAuthProvider');
  }
  return context;
};

export const BackendAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [smmParameters, setSmmParameters] = useState(null);
  const [salutationCatalog, setSalutationCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [companyValid, setCompanyValid] = useState(null); // null = unknown/loading, true/false after SMM fetch
  const [dataVersion, setDataVersion] = useState(0); // increments on refresh to trigger dashboard reload
  const salutationRequestsRef = useRef(new Map());
  const salutationScopeRef = useRef(null);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if we have an error from the auth callback
        const urlParams = new URLSearchParams(window.location.search);
        const authError = urlParams.get('error');
        if (authError) {
          console.log('Auth error from URL:', authError);
          setError(decodeURIComponent(authError));
          setLoading(false);
          return;
        }

        // Check if we have a token from the auth callback
        const token = urlParams.get('token');
        if (token) {
          try {
            authService.setToken(token);
            await verifyAndSetUser(token);

            // Restore the URL that was stored before login
            const pendingParams = localStorage.getItem('pendingUrlParams');
            const pendingPath = localStorage.getItem('pendingUrlPath') || window.location.pathname;
            if (pendingParams || pendingPath !== window.location.pathname) {
              console.log('Auth: Restoring pending URL:', pendingPath, pendingParams);
              localStorage.removeItem('pendingUrlParams');
              localStorage.removeItem('pendingUrlPath');
              const newUrl = `${pendingPath}${pendingParams ? `?${pendingParams}` : ''}`;
              console.log('Auth: Setting URL to:', newUrl);
              window.history.replaceState({}, document.title, newUrl);

              // Trigger a custom event to notify components of URL change
              window.dispatchEvent(new CustomEvent('urlRestored', { detail: { params: pendingParams } }));
            } else {
              console.log('Auth: No pending URL parameters, cleaning up URL');
              localStorage.removeItem('pendingUrlPath');
              // Clean up URL and redirect to home after successful auth
              window.history.replaceState({}, document.title, window.location.pathname);
            }
            return; // Exit early after handling token
          } catch (error) {
            console.error('Token handling error:', error);
            setError(error.message || 'Authentication failed');
            setLoading(false);
            return;
          }
        }

        // Check if user is already authenticated (check cookies first, then localStorage)
        if (authService.isAuthenticated()) {
          // Try to restore SMM parameters from localStorage first
          const storedSmmParams = localStorage.getItem('smmParameters');
          if (storedSmmParams) {
            try {
              setSmmParameters(JSON.parse(storedSmmParams));
              console.log('Restored SMM parameters from localStorage');
            } catch (e) {
              console.error('Failed to parse stored SMM parameters:', e);
            }
          }

          // Try to get user from cookies first
          const userFromCookies = authService.getUserFromCookies();
          if (userFromCookies) {
            setUser(userFromCookies);
            setIsAuthenticated(true);
            // Fetch fresh SMM parameters and user language preference
            await fetchSmmParameters();
            await fetchAndSetUserLanguage();
            setLoading(false);
          } else {
            // Fallback to API call
            try {
              const result = await authService.getCurrentUser();
              if (result.success) {
                setUser(result.user);
                setIsAuthenticated(true);
                // Fetch SMM parameters and user language after successful authentication
                await fetchSmmParameters();
                await fetchAndSetUserLanguage();
              }
            } catch (error) {
              console.error('Failed to get user from API, trying token verification:', error);
              // Final fallback to token verification
              const token = authService.getToken();
              if (token) {
                await verifyAndSetUser(token);
              }
            }
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        setError(error.message);
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    const email = user?.email?.trim();
    const company = smmParameters?.mserp_dataareaid?.trim();

    if (!isAuthenticated || !email || !company) {
      setSalutationCatalog(null);
      if (!isAuthenticated) {
        salutationRequestsRef.current.clear();
        salutationScopeRef.current = null;
      }
      return;
    }

    const scopeKey = `${email.toLocaleLowerCase()}|${company.toLocaleLowerCase()}`;
    if (salutationScopeRef.current && salutationScopeRef.current !== scopeKey) {
      salutationRequestsRef.current.clear();
      setSalutationCatalog(null);
    }
    salutationScopeRef.current = scopeKey;

    let isCurrent = true;
    let request = salutationRequestsRef.current.get(scopeKey);
    if (!request) {
      request = dataService.getSalutations();
      salutationRequestsRef.current.set(scopeKey, request);
    }

    request.then((result) => {
      if (!isCurrent || salutationScopeRef.current !== scopeKey) return;

      if (!result.success) {
        salutationRequestsRef.current.delete(scopeKey);
      }

      setSalutationCatalog({
        scopeKey,
        enabled: result.success && result.enabled === true,
        data: result.success && Array.isArray(result.data) ? result.data : []
      });
    });

    return () => {
      isCurrent = false;
    };
  }, [isAuthenticated, user?.email, smmParameters?.mserp_dataareaid]);

  const fetchAndSetUserLanguage = async () => {
    try {
      const langResult = await dataService.getUserLanguage();
      if (langResult.success && langResult.language) {
        const appLang = mapLocaleToLanguage(langResult.language);
        console.log(`User language from Dataverse: ${langResult.language} → app language: ${appLang}`);
        changeLanguage(appLang);
      } else {
        console.log('No language preference found in Dataverse, using default');
      }
    } catch (error) {
      console.error('Error fetching user language:', error);
    }
  };

  const fetchSmmParameters = async ({ refresh = false } = {}) => {
    try {
      console.log(`Fetching SMM parameters...${refresh ? ' (forced refresh)' : ''}`);
      const result = await dataService.getSmmParameters({ refresh });
      if (result.success) {
        console.log('SMM parameters fetched successfully:', result.data);
        console.log('  - Allow Opportunity Field:', result.data?.mserp_alg_allowopportunityfield);
        console.log('  - Automatic Sequence Number:', result.data?.mserp_alg_automaticseqnum);
        setSmmParameters(result.data);
        // Store in localStorage for persistence across page refreshes
        localStorage.setItem('smmParameters', JSON.stringify(result.data));
        // Evaluate company validity - block restricted companies
        const company = result.data?.mserp_dataareaid;
        const restrictedCompanies = ['dat', '005', 'cor', 'tra'];
        if (company && !restrictedCompanies.includes(company.toLowerCase())) {
          setCompanyValid(true);
        } else {
          console.warn('Invalid or restricted company detected:', company);
          setCompanyValid(false);
        }
      } else {
        console.warn('Failed to fetch SMM parameters:', result.error);
        setCompanyValid(false);
      }
    } catch (error) {
      console.error('Error fetching SMM parameters:', error);
      setCompanyValid(false);
    }
  };

  const verifyAndSetUser = async (token) => {
    try {
      const result = await authService.verifyToken(token);
      if (result.success) {
        setUser(result.user);
        setIsAuthenticated(true);
        // Fetch SMM parameters and user language after successful authentication
        await fetchSmmParameters();
        await fetchAndSetUserLanguage();
      } else {
        authService.removeToken();
        setError(result.error);
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      authService.removeToken();
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const login = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('Getting login URL from backend...');

      // Store current URL parameters to restore after login
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.delete('token');
      currentParams.delete('error');

      if (currentParams.toString()) {
        console.log('Auth: Storing URL parameters before login:', currentParams.toString());
        localStorage.setItem('pendingUrlParams', currentParams.toString());
      }
      if (window.location.pathname !== '/') {
        localStorage.setItem('pendingUrlPath', window.location.pathname);
      }

      const authUrl = await authService.getLoginUrl();
      console.log('Backend returned auth URL:', authUrl);
      // Redirect to backend auth URL
      window.location.href = authUrl;
    } catch (error) {
      console.error('Login failed:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  const logout = async () => {
    salutationRequestsRef.current.clear();
    salutationScopeRef.current = null;
    setSalutationCatalog(null);

    try {
      const result = await authService.logout();
      authService.removeToken();
      apiService.clearCache();
      setUser(null);
      setIsAuthenticated(false);
      setSmmParameters(null);
      // Clear SMM parameters from localStorage
      localStorage.removeItem('smmParameters');

      if (result.success && result.logoutUrl) {
        // Redirect to Entra ID logout
        window.location.href = result.logoutUrl;
      }
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout fails, clear local state
      authService.removeToken();
      apiService.clearCache();
      setUser(null);
      setIsAuthenticated(false);
      setSmmParameters(null);
      localStorage.removeItem('smmParameters');
    }
  };

  const refreshData = async () => {
    await fetchSmmParameters({ refresh: true });
    setDataVersion(v => v + 1);
  };

  const value = {
    isAuthenticated,
    user,
    smmParameters,
    salutationCatalog,
    companyValid,
    dataVersion,
    loading,
    error,
    login,
    logout,
    refreshData
  };

  return (
    <BackendAuthContext.Provider value={value}>
      {children}
    </BackendAuthContext.Provider>
  );
};
